import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 select-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        outline: 'border border-border text-foreground',
        destructive: 'bg-destructive/15 text-destructive border border-destructive/20',
        warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/20',
        success: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20',
        info: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/20',
        purple: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border border-purple-500/20',
        cyan: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border border-cyan-500/20',
        processing: 'bg-primary/15 text-primary border border-primary/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className={cn(
            'inline-block size-1.5 rounded-full',
            variant === 'processing'
              ? 'bg-primary animate-pulse'
              : variant === 'success'
              ? 'bg-emerald-500'
              : variant === 'warning'
              ? 'bg-amber-500'
              : variant === 'destructive'
              ? 'bg-red-500'
              : 'bg-current'
          )}
        />
      )}
      {children}
    </div>
  );
}
